import { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { X, Download, Link2, Check, Twitter, Linkedin, Copy, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { createPermanentReceipt, trackReceiptEvent } from '@/lib/persistentReceipts';
import { buildLegacyReceiptUrl, buildReceiptUrl, getReceiptCode } from '@/lib/receipts';
import type { LegacyQuoteCard, PermanentReceipt } from '@/types';

interface ShareCardModalProps {
  card: LegacyQuoteCard | null;
  searchQuery?: string;
  ownerId?: string | null;
  existingReceipt?: PermanentReceipt | null;
  isOpen: boolean;
  onReceiptReady?: (receipt: PermanentReceipt) => void;
  onClose: () => void;
}

export function ShareCardModal({
  card,
  searchQuery,
  ownerId,
  existingReceipt,
  isOpen,
  onReceiptReady,
  onClose,
}: ShareCardModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPersisting, setIsPersisting] = useState(false);
  const [persistError, setPersistError] = useState('');
  const [permanentReceipt, setPermanentReceipt] = useState<PermanentReceipt | null>(null);
  const [copied, setCopied] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return;
    setIsGenerating(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      setImageUrl(dataUrl);
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && card) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.2, ease: 'power2.out' }
      );
      gsap.fromTo(
        modalRef.current,
        { scale: 0.95, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [isOpen, card]);

  useEffect(() => {
    let isCurrent = true;

    const persistReceipt = async () => {
      if (!isOpen || !card) return;

      setImageUrl(null);
      setPersistError('');

      if (existingReceipt) {
        setPermanentReceipt(existingReceipt);
        setIsPersisting(false);
        return;
      }

      setPermanentReceipt(null);
      setIsPersisting(true);
      try {
        const storedReceipt = await createPermanentReceipt(card, searchQuery, ownerId);
        if (isCurrent) {
          setPermanentReceipt(storedReceipt);
          onReceiptReady?.(storedReceipt);
        }
      } catch (error: unknown) {
        if (isCurrent)
          setPersistError(
            error instanceof Error ? error.message : 'Permanent receipt storage is unavailable'
          );
      } finally {
        if (isCurrent) setIsPersisting(false);
      }
    };

    void persistReceipt();

    return () => {
      isCurrent = false;
    };
  }, [card, existingReceipt, isOpen, onReceiptReady, ownerId, searchQuery]);

  useEffect(() => {
    if (!isOpen || !card || (isPersisting && !permanentReceipt)) return;
    void generateImage();
  }, [card, generateImage, isOpen, isPersisting, permanentReceipt]);

  const permanentReceiptUrl = permanentReceipt
    ? buildReceiptUrl(permanentReceipt.id)
    : card
      ? buildLegacyReceiptUrl(card, searchQuery)
      : '';

  const recordShare = (method: string) => {
    if (permanentReceipt) {
      void trackReceiptEvent(permanentReceipt.id, 'shared', { method });
    }
  };

  const handleClose = () => {
    gsap.to(modalRef.current, {
      scale: 0.95,
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.2,
      delay: 0.1,
      ease: 'power2.in',
      onComplete: () => {
        onClose();
        setImageUrl(null);
        setCopied(false);
      },
    });
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.download = `receipt-${card?.id || 'card'}.png`;
    link.href = imageUrl;
    link.click();
    recordShare('download_png');
  };

  const handleCopyImage = async () => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      recordShare('copy_image');
    } catch (error) {
      console.error('Error copying image:', error);
    }
  };

  const handleCopyLink = async () => {
    if (!card) return;
    try {
      await navigator.clipboard.writeText(permanentReceiptUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      recordShare('copy_link');
    } catch (error) {
      console.error('Error copying link:', error);
    }
  };

  const handleShareToX = () => {
    if (!card) return;
    const text = `"${card.quote}" — ${card.source}`;
    const destination = new URL('https://twitter.com/intent/tweet');
    destination.searchParams.set('text', text);
    destination.searchParams.set('url', permanentReceiptUrl);
    window.open(destination.toString(), '_blank', 'noopener,noreferrer');
    recordShare('x');
  };

  const handleShareToLinkedIn = () => {
    if (!card) return;
    const destination = new URL('https://www.linkedin.com/sharing/share-offsite/');
    destination.searchParams.set('url', permanentReceiptUrl);
    window.open(destination.toString(), '_blank', 'noopener,noreferrer');
    recordShare('linkedin');
  };

  const handleNativeShare = async () => {
    if (!card) return;

    const url = permanentReceiptUrl;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Receipt ${getReceiptCode(permanentReceipt?.id ?? card.id)}`,
          text: `Primary-source evidence from ${card.source}`,
          url,
        });
        recordShare('native_share');
        return;
      }

      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      recordShare('native_copy_fallback');
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === 'AbortError') return;
      console.error('Error sharing receipt:', shareError);
    }
  };

  if (!isOpen || !card) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[300] flex items-center justify-center bg-[#0B0D10]/60 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === overlayRef.current && handleClose()}
    >
      <div
        ref={modalRef}
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgba(11,13,16,0.08)] p-6">
          <div>
            <h2 className="font-display text-xl font-bold text-[#0B0D10]">Share Receipt</h2>
            <p className="text-sm text-[#6B6F78]">Generate a clean citation card</p>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 transition-colors hover:bg-[#F6F5F2]"
            aria-label="Close share receipt"
          >
            <X className="h-5 w-5 text-[#6B6F78]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Preview Card */}
            <div>
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                Preview
              </p>

              {/* The Share Card */}
              <div
                ref={cardRef}
                className="flex flex-col overflow-hidden rounded-xl bg-white shadow-xl"
                style={{ aspectRatio: '9/16', maxWidth: '300px' }}
              >
                {/* Red Header */}
                <div className="flex items-center justify-between bg-[#E1062C] p-4">
                  <span className="font-display text-lg font-bold tracking-[0.05em] text-white">
                    RECEIPT
                  </span>
                  <span className="font-mono text-[10px] text-white">
                    {getReceiptCode(permanentReceipt?.id ?? card.id)}
                  </span>
                </div>

                {/* Card Body */}
                <div className="flex min-h-0 flex-1 flex-col p-5">
                  {/* Lane Badge */}
                  <div className="mb-4 flex items-center gap-2">
                    <div className="rounded bg-[#F6F5F2] px-2 py-1 font-mono text-xs text-[#6B6F78]">
                      Lane {card.lane}
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="flex-1">
                    {searchQuery && (
                      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.1em] text-[#6B6F78]">
                        Evidence for: {searchQuery}
                      </p>
                    )}
                    <p className="max-h-64 overflow-hidden text-sm font-medium leading-relaxed text-[#0B0D10]">
                      "{card.quote}"
                    </p>
                  </div>

                  {/* Source Info */}
                  <div className="mt-4 border-t border-[rgba(11,13,16,0.08)] pt-4">
                    <p className="text-sm font-medium text-[#0B0D10]">{card.source}</p>
                    <p className="mt-1 font-mono text-xs text-[#6B6F78]">{card.documentTitle}</p>
                    <p className="mt-1 font-mono text-xs text-[#E1062C]">{card.date}</p>
                  </div>

                  {/* Canonical source marker */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#0B0D10]">
                      <Link2 className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-xs text-[#6B6F78]">Canonical source</p>
                      <p className="font-mono text-xs text-[#E1062C]">Link included</p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-[#F6F5F2] px-4 py-2">
                  <p className="text-center font-mono text-[10px] text-[#6B6F78]">
                    Generated by Duly Cited
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col">
              <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                Export Options
              </p>

              <div className="flex-1 space-y-3">
                <div
                  className={`rounded-xl border p-3 text-sm ${
                    permanentReceipt
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                      : persistError
                        ? 'border-amber-200 bg-amber-50 text-amber-800'
                        : 'border-black/10 bg-[#F6F5F2] text-[#6B6F78]'
                  }`}
                  role="status"
                >
                  {permanentReceipt
                    ? `Permanent receipt saved · ${getReceiptCode(permanentReceipt.id)}`
                    : persistError
                      ? 'Permanent storage is unavailable. Sharing will use a live lookup link.'
                      : 'Saving a permanent receipt…'}
                </div>

                <Button
                  onClick={handleDownload}
                  disabled={isGenerating || !imageUrl}
                  className="flex w-full items-center justify-center gap-2 bg-[#E1062C] py-6 text-white hover:bg-[#c00525]"
                >
                  <Download className="h-5 w-5" />
                  {isGenerating ? 'Generating...' : 'Download PNG'}
                </Button>

                <Button
                  onClick={handleCopyImage}
                  disabled={isGenerating || !imageUrl}
                  variant="outline"
                  className="flex w-full items-center justify-center gap-2 border-[#0B0D10] py-6 text-[#0B0D10] hover:bg-[#0B0D10] hover:text-white"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                  {copied ? 'Copied!' : 'Copy Image'}
                </Button>

                <Button
                  onClick={handleNativeShare}
                  disabled={isPersisting}
                  variant="outline"
                  className="flex w-full items-center justify-center gap-2 border-[#0B0D10] py-6 text-[#0B0D10] hover:bg-[#0B0D10] hover:text-white"
                >
                  <Smartphone className="h-5 w-5" />
                  Share from this device
                </Button>

                <div className="border-t border-[rgba(11,13,16,0.08)] pt-4">
                  <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Share to
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={handleShareToX}
                      disabled={isPersisting}
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#1DA1F2] p-3 text-white transition-opacity hover:opacity-90"
                    >
                      <Twitter className="h-5 w-5" />X
                    </button>
                    <button
                      onClick={handleShareToLinkedIn}
                      disabled={isPersisting}
                      className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#0A66C2] p-3 text-white transition-opacity hover:opacity-90"
                    >
                      <Linkedin className="h-5 w-5" />
                      LinkedIn
                    </button>
                  </div>
                </div>

                <div className="border-t border-[rgba(11,13,16,0.08)] pt-4">
                  <p className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Public receipt
                  </p>
                  <button
                    onClick={handleCopyLink}
                    disabled={isPersisting}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F6F5F2] p-3 text-[#0B0D10] transition-colors hover:bg-[#0B0D10] hover:text-white"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                    {isPersisting
                      ? 'Saving permanent receipt…'
                      : copied
                        ? 'Copied!'
                        : 'Copy receipt link'}
                  </button>
                </div>
              </div>

              {/* Source Info */}
              <div className="mt-4 rounded-xl bg-[#F6F5F2] p-4">
                <p className="text-xs text-[#6B6F78]">Canonical source:</p>
                <a
                  href={card.canonicalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-sm text-[#E1062C] hover:underline"
                >
                  {card.canonicalLink}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
